/** A list whose last row left holds the focus for the empty state this long (the row's exit and its snapshot). */
const HOLD_MS = 3000;
const HELD = "data-focus-held";
/**
 * The element holding it, since when, and the region its empty state shows
 * up in (the dialog it is in, else the page's `main`): an empty state
 * anywhere else (a sheet that opens empty) never takes it.
 */
let held: { el: HTMLElement; at: number; scope: Element } | null = null;

/**
 * The last row of a list left with the focus on it and nothing in the list
 * can take it (P15, ux-a11y-perf-02): the focus waits on `el` (the list, or
 * the dialog around it: `tabindex="-1"` while it holds it, never a Tab stop)
 * for the `EmptyState` that replaces the list, which takes it as it mounts
 * (`claimHeldFocus`), also when the list unmounted with it, within 3 s.
 * Returns whether `el` took the focus.
 */
export function holdFocusForEmpty(el: HTMLElement | null | undefined): boolean {
  if (!el || !el.isConnected || typeof document === "undefined") return false;
  const added = !el.hasAttribute("tabindex");
  if (added) el.setAttribute("tabindex", "-1");
  el.setAttribute(HELD, "");
  el.focus({ preventScroll: true });
  if (document.activeElement !== el) {
    el.removeAttribute(HELD);
    if (added) el.removeAttribute("tabindex");
    return false;
  }
  held = { el, at: Date.now(), scope: el.closest('[role="dialog"], main') ?? document.body };
  el.addEventListener(
    "blur",
    (event) => {
      el.removeAttribute(HELD);
      if (added) el.removeAttribute("tabindex");
      // The person moved it on to another control: nothing waits for an empty state any more.
      const next = event.relatedTarget;
      if (next instanceof Element && next !== document.body && held?.el === el) held = null;
    },
    { once: true },
  );
  return true;
}

/**
 * An empty state mounting (`root`) asks whether the focus waits for it: on a
 * list that holds it, or dropped to <body> with the list that held it a
 * moment ago (and not moved on since), and only in that list's region (its
 * dialog, the page's `main`). True once: the empty state that asks takes it.
 */
export function claimHeldFocus(root: Element | null): boolean {
  if (typeof document === "undefined" || !held || !root) return false;
  const active = document.activeElement;
  const waiting =
    (active === held.el && held.el.hasAttribute(HELD)) ||
    ((!active || active === document.body) && Date.now() - held.at < HOLD_MS);
  if (!waiting || !held.scope.isConnected || !held.scope.contains(root)) return false;
  held = null;
  return true;
}
